import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import OwnerLayout from '../../components/OwnerLayout';
import api from '../../utils/api';

const emptyForm = { name: '', category_id: '', price: '', description: '', photo: '', is_veg: true, is_available: true };

export default function OwnerMenuItems() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('all');

  const load = async () => {
    try {
      const [iRes, cRes] = await Promise.all([api.get('/api/owner/items'), api.get('/api/owner/categories')]);
      setItems(iRes.data);
      setCategories(cRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ ...item, price: String(item.price) }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category_id || !form.price) return toast.error('Name, category and price are required');
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editItem) {
        await api.patch(`/api/owner/items/${editItem.id}`, payload);
        toast.success('Item updated!');
      } else {
        await api.post('/api/owner/items', payload);
        toast.success('Item added!');
      }
      setShowForm(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/api/owner/items/${id}`);
      toast.success('Deleted!');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleAvail = async (item) => {
    try {
      await api.patch(`/api/owner/items/${item.id}`, { is_available: !item.is_available });
      load();
    } catch { toast.error('Failed to update'); }
  };

  const getCatName = (id) => categories.find(c => c.id === id)?.name || '';
  const filtered = filterCat === 'all' ? items : items.filter(i => i.category_id === filterCat);

  return (
    <OwnerLayout title="Menu Items">
      <div className="fade-in">
        {/* Filter */}
        <div style={styles.filterRow}>
          <button
            style={{ ...styles.filterBtn, ...(filterCat === 'all' ? styles.filterActive : {}) }}
            onClick={() => setFilterCat('all')}
          >All</button>
          {categories.map(c => (
            <button
              key={c.id}
              style={{ ...styles.filterBtn, ...(filterCat === c.id ? styles.filterActive : {}) }}
              onClick={() => setFilterCat(c.id)}
            >{c.name}</button>
          ))}
        </div>

        {/* Add Button */}
        <button className="btn btn-primary btn-full" onClick={openAdd} style={{ marginBottom: 16 }}>
          <Plus size={18} /> Add Menu Item
        </button>

        {/* Form Modal */}
        {showForm && (
          <div style={styles.overlay}>
            <div style={styles.modal} className="slide-up">
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{editItem ? 'Edit Item' : 'Add New Item'}</h3>
                <button onClick={() => setShowForm(false)} style={styles.closeBtn}><X size={20} /></button>
              </div>
              <form onSubmit={handleSave} style={styles.modalForm}>
                <div className="form-group">
                  <label className="label">Item Name *</label>
                  <input className="input" placeholder="e.g. Butter Chicken" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Category *</label>
                  <select className="input" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Price (₹) *</label>
                  <input className="input" type="number" placeholder="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Description</label>
                  <textarea className="input" placeholder="Short description (optional)" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ resize: 'none' }} />
                </div>
                <div className="form-group">
                  <label className="label">Photo URL</label>
                  <input className="input" placeholder="https://..." value={form.photo} onChange={e => setForm({ ...form, photo: e.target.value })} />
                </div>
                <div style={styles.toggleRow}>
                  <div style={styles.toggleItem}>
                    <span style={styles.toggleLabel}>Type</span>
                    <div style={styles.vegToggle}>
                      <button type="button" style={{ ...styles.vegBtn, ...(form.is_veg ? styles.vegActive : {}) }} onClick={() => setForm({ ...form, is_veg: true })}>🟢 Veg</button>
                      <button type="button" style={{ ...styles.vegBtn, ...(!form.is_veg ? styles.nonVegActive : {}) }} onClick={() => setForm({ ...form, is_veg: false })}>🔴 Non-Veg</button>
                    </div>
                  </div>
                  <div style={styles.toggleItem}>
                    <span style={styles.toggleLabel}>Available</span>
                    <label style={styles.switch}>
                      <input type="checkbox" checked={form.is_available} onChange={e => setForm({ ...form, is_available: e.target.checked })} style={{ display: 'none' }} />
                      <div style={{ ...styles.switchTrack, background: form.is_available ? '#27AE60' : '#ECF0F1' }}>
                        <div style={{ ...styles.switchThumb, left: form.is_available ? '22px' : '2px' }} />
                      </div>
                    </label>
                  </div>
                </div>
                <button className="btn btn-primary btn-full" type="submit" disabled={saving}>
                  {saving ? <span className="spinner" style={{ width: 18, height: 18 }} /> : editItem ? 'Save Changes' : 'Add Item'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Items List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><h3>No items</h3><p>Add your first menu item</p></div>
        ) : (
          filtered.map(item => (
            <div key={item.id} className="card" style={{ ...styles.itemCard, opacity: item.is_available ? 1 : 0.6 }}>
              <div style={styles.itemRow}>
                {item.photo && <img src={item.photo} alt="" style={styles.itemPhoto} />}
                <div style={styles.itemInfo}>
                  <div style={styles.itemNameRow}>
                    <span style={{ fontSize: 12 }}>{item.is_veg ? '🟢' : '🔴'}</span>
                    <span style={styles.itemName}>{item.name}</span>
                  </div>
                  <p style={styles.itemCat}>{getCatName(item.category_id)}</p>
                  {item.description && <p style={styles.itemDesc}>{item.description}</p>}
                  <div style={styles.itemBottom}>
                    <span style={styles.itemPrice}>₹{item.price}</span>
                    {!item.is_available && <span className="badge badge-danger" style={{ fontSize: 10 }}>Unavailable</span>}
                  </div>
                </div>
                <div style={styles.itemActions}>
                  <button className="btn btn-secondary btn-sm" onClick={() => toggleAvail(item)} style={{ fontSize: 11, padding: '6px 10px' }}>
                    {item.is_available ? 'Hide' : 'Show'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(item)}><Pencil size={14} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id, item.name)}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </OwnerLayout>
  );
}

const styles = {
  filterRow: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 12 },
  filterBtn: { padding: '6px 16px', borderRadius: 100, fontSize: 13, fontWeight: 500, background: 'white', border: '1.5px solid #ECF0F1', color: '#7F8C8D', whiteSpace: 'nowrap', cursor: 'pointer' },
  filterActive: { background: '#FF6B35', color: 'white', border: '1.5px solid #FF6B35' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modal: { background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 0' },
  modalTitle: { fontSize: '17px', fontWeight: '600', color: '#2C3E50' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#7F8C8D' },
  modalForm: { padding: '16px 20px 32px' },
  toggleRow: { display: 'flex', gap: 16, marginBottom: 16 },
  toggleItem: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8 },
  toggleLabel: { fontSize: 13, fontWeight: 500, color: '#2C3E50' },
  vegToggle: { display: 'flex', gap: 6 },
  vegBtn: { flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, border: '1.5px solid #ECF0F1', background: 'white', cursor: 'pointer' },
  vegActive: { background: '#e8f8ef', border: '1.5px solid #27AE60', color: '#27AE60', fontWeight: 600 },
  nonVegActive: { background: '#fdecea', border: '1.5px solid #E74C3C', color: '#E74C3C', fontWeight: 600 },
  switch: { cursor: 'pointer' },
  switchTrack: { width: 46, height: 26, borderRadius: 100, position: 'relative', transition: 'background 0.2s' },
  switchThumb: { position: 'absolute', top: 3, width: 20, height: 20, background: 'white', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' },
  itemCard: { marginBottom: 10, transition: 'opacity 0.2s' },
  itemRow: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  itemPhoto: { width: 70, height: 70, borderRadius: 10, objectFit: 'cover', flexShrink: 0 },
  itemInfo: { flex: 1, minWidth: 0 },
  itemNameRow: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 },
  itemName: { fontSize: '14px', fontWeight: '600', color: '#2C3E50' },
  itemCat: { fontSize: '11px', color: '#FF6B35', marginBottom: 2 },
  itemDesc: { fontSize: '12px', color: '#7F8C8D', marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  itemBottom: { display: 'flex', alignItems: 'center', gap: 8 },
  itemPrice: { fontSize: '15px', fontWeight: '700', color: '#2C3E50' },
  itemActions: { display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 },
};
